'use client';

import { useEffect, useMemo, useState } from 'react';
import { Loader2, Link2, RefreshCw, Copy } from 'lucide-react';
import { Button } from '@/components/ui/forms/button';
import { Input } from '@/components/ui/forms/input';
import { Switch } from '@/components/ui/forms/switch';
import { useToast } from '@/hooks/use-toast';
import type { ResumeShare } from '@/lib/resume/types';

interface ResumeShareControlsProps {
  /** Whether the surrounding dialog is open; share state is loaded on open. */
  active: boolean;
  resumeId: string;
  initialShare?: ResumeShare;
  onShareChange?: (share: ResumeShare) => void;
  /** Reports the busy state so the parent can disable its own controls. */
  onBusyChange?: (busy: boolean) => void;
}

interface ShareApiResponse {
  share?: ResumeShare;
  error?: string;
  details?: string;
}

function getAbsoluteShareUrl(path: string | null): string {
  if (!path) return '';
  if (typeof window === 'undefined') return path;
  return `${window.location.origin}${path}`;
}

/**
 * Picks the enable toast copy based on whether the auto-copy-to-clipboard
 * actually succeeded. Kept pure (no DOM/clipboard access) so it can be
 * unit tested without a browser environment.
 */
export function buildEnableToast(copied: boolean): { title: string; description: string } {
  return copied
    ? { title: 'Sharing enabled', description: 'Your resume can now be viewed with the link. Link copied to clipboard.' }
    : { title: 'Sharing enabled', description: 'Your resume can now be viewed with the link.' };
}

/** Same idea as {@link buildEnableToast}, for the regenerate action. */
export function buildRegenerateToast(copied: boolean): { title: string; description: string } {
  return copied
    ? { title: 'Link regenerated', description: 'Old shared link is no longer valid. New link copied to clipboard.' }
    : { title: 'Link regenerated', description: 'Old shared link is no longer valid.' };
}

/**
 * Link-sharing controls for a resume.
 *
 * Extracted so the share-only dialog on the resumes hub and the combined
 * share-and-export dialog in the builder stay one implementation rather than
 * two that drift apart.
 */
export function ResumeShareControls({
  active,
  resumeId,
  initialShare,
  onShareChange,
  onBusyChange,
}: ResumeShareControlsProps) {
  const { toast } = useToast();
  const [share, setShare] = useState<ResumeShare>(initialShare ?? { enabled: false, path: null });
  const [isLoading, setIsLoading] = useState(false);
  const [isMutating, setIsMutating] = useState(false);

  useEffect(() => {
    setShare(initialShare ?? { enabled: false, path: null });
  }, [initialShare, resumeId]);

  useEffect(() => {
    if (!active) return;

    const loadState = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(`/api/resume-builder/${resumeId}/share`, { cache: 'no-store' });
        const payload = (await response.json().catch(() => null)) as ShareApiResponse | null;
        if (!response.ok || !payload?.share) {
          throw new Error(payload?.error || payload?.details || 'Failed to load share settings');
        }

        setShare(payload.share);
      } catch (error) {
        console.error('Failed to load share settings:', error);
        toast({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to load share settings',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    void loadState();
  }, [active, resumeId, toast]);

  const busy = isLoading || isMutating;

  useEffect(() => {
    onBusyChange?.(busy);
  }, [busy, onBusyChange]);

  const absoluteUrl = useMemo(() => getAbsoluteShareUrl(share.path), [share.path]);

  const mutateShare = async (action: 'enable' | 'disable' | 'regenerate') => {
    setIsMutating(true);
    try {
      const response = await fetch(`/api/resume-builder/${resumeId}/share`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      });
      const payload = (await response.json().catch(() => null)) as ShareApiResponse | null;
      if (!response.ok || !payload?.share) {
        throw new Error(payload?.error || payload?.details || 'Failed to update share settings');
      }

      setShare(payload.share);
      onShareChange?.(payload.share);

      if (action === 'disable') {
        toast({ title: 'Sharing disabled', description: 'The public link has been revoked.' });
      } else {
        // Sharing is already confirmed enabled at this point (state above is
        // updated and the server call succeeded), so a clipboard failure here
        // is purely cosmetic — it must never surface as an error and must
        // never be reported as "copied" when it wasn't. Copy from the
        // response payload directly rather than the `absoluteUrl` memo,
        // since React state from setShare() above may not have flushed yet.
        let copied = false;
        const urlToCopy = getAbsoluteShareUrl(payload.share.path);
        if (urlToCopy) {
          try {
            await navigator.clipboard.writeText(urlToCopy);
            copied = true;
          } catch (copyError) {
            console.error('Failed to auto-copy share link:', copyError);
          }
        }

        toast(action === 'regenerate' ? buildRegenerateToast(copied) : buildEnableToast(copied));
      }
    } catch (error) {
      console.error('Failed to update share settings:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update share settings',
        variant: 'destructive',
      });
    } finally {
      setIsMutating(false);
    }
  };

  const handleCopyLink = async () => {
    if (!absoluteUrl) return;

    try {
      await navigator.clipboard.writeText(absoluteUrl);
      toast({ title: 'Link copied', description: 'Shared resume URL copied to clipboard.' });
    } catch {
      toast({ title: 'Failed to copy', description: 'Could not copy the link.', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between rounded-md border p-3">
        <div>
          <p className="text-sm font-medium">Share by link</p>
          <p className="text-xs text-muted-foreground">
            Anyone with the link can view and download this resume.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isMutating && (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Updating...
            </span>
          )}
          <Switch
            checked={share.enabled}
            disabled={isMutating}
            onCheckedChange={(checked) => {
              if (checked === share.enabled) return;
              void mutateShare(checked ? 'enable' : 'disable');
            }}
          />
        </div>
      </div>

      {share.enabled && (
        <div className="space-y-2">
          <label className="text-xs font-medium text-muted-foreground">Shared URL</label>
          <div className="flex items-center gap-2">
            <Input value={absoluteUrl} readOnly className="font-mono text-xs" />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCopyLink}
              disabled={!absoluteUrl || busy}
              aria-label="Copy shared link"
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void mutateShare('regenerate')}
              disabled={busy}
            >
              {isMutating ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Regenerate link
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void mutateShare('disable')}
              disabled={busy}
            >
              <Link2 className="mr-2 h-4 w-4" />
              Disable sharing
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
